/**
 * Route guard for the `/sa/*` namespace — Epic 38 Story 38.3.
 *
 * **Auth posture (matches the 38.2 trust-the-backend pattern):** the
 * guard only enforces "is authenticated". The backend is the
 * authoritative SA gate — every `/api/sa/*` endpoint returns 403 to
 * non-SA principals. The SA store catches 403 responses and emits a
 * user-visible error.
 *
 * Rationale (per Story 38.3 AC10 Open Question 1 + dev-kickoff
 * resolution 2026-05-15): the existing `User` type doesn't expose a
 * clean "is SA" flag, and the `*hasPermission` directive is still on
 * legacy CompanyUser flags (38.2 Discovery #5). Until the directive +
 * the auth store pick up the atom engine in a future cross-cut, the
 * pragmatic FE story is "auth-only guard + backend 403 = authoritative
 * gate". This matches how 38.2 handles the analogous case for influencer
 * routes (anyone authenticated can navigate; the route handler 403s
 * non-owners).
 */
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { inject } from '@angular/core';

import { AuthenticationService } from '@lib-common/services/authentication.service';

export const SaGuard: CanActivateFn = (_route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const auth = inject(AuthenticationService);
  const router = inject(Router);

  const user = auth.getCurrentUser;
  if (!user) {
    if (auth.isZitadelEnabled()) {
      // Deep-link the SA user back to the /sa/* route they wanted, post-login (FR-LOGIN5) —
      // same OIDC-state capture as AppAuthGuard.
      auth.loginWithZitadel({ returnUrl: state.url });
      return false;
    }
    return router.parseUrl('/authentication/login');
  }
  return true;
};
